import { useContext, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import store from 'client/store/store';
import { useLoaderData } from 'react-router';
import { useBaseMap } from 'client/pages/MapPage/layers/sources/BaseMap';
import VectorSource from 'ol/source/Vector';
import { modifyStyle } from 'client/pages/MapPage/layers/styles';
import AssetLayers from 'client/pages/MapPage/components/AssetLayers/AssetLayers';
import { useMaxDrawExtent, useLineDrawDimensions } from 'client/utils/auth/userPermissions';
import { mapModes } from 'client/utils/constants/appStates';
import { ASSET_TYPES } from 'client/utils/constants/assetTypes';
import {
    Layers,
    MapContext,
    TileLayer,
    VectorLayer
} from 'os-elements';
import { backdropStyle, borderStyle, enhancedMeasuresBackdropStyle, hazardTapeBorderStyle } from 'client/pages/MapPage/layers/styles/aoiStyle';
import { positionMarkerStyle } from 'client/pages/MapPage/layers/styles/locationMarkerStyle';
import CustomVectorSource from 'client/pages/MapPage/layers/CustomVectorSource';
import CustomBackdropMap from 'client/pages/MapPage/layers/CustomBackdropMap';
import OneTapAOIIndicator from 'client/pages/MapPage/components/OneTapAOIIndicator/OneTapAOIIndicator';
import { isNi } from 'client/utils/coords/coordUtils';
import { useDarkMode } from 'client/utils/helpers/DarkMode/useDarkMode';
import styles from 'client/pages/MapPage/MapPage.module.scss';
import {
    ANNOTATIONS_LAYER_NAME,
    DIMENSIONS_LAYER_NAME,
    LAYER_CONFIG,
    NI_ANNOTATIONS_LAYER_NAME,
    NI_DIMENSIONS_LAYER_NAME
} from 'client/pages/MapPage/layers/constants';
import { useEnhancedMeasures } from 'client/pages/MapPage/components/EnhancedMeasuresModal/useEnhancedMeasures';
import { selectBufferRadiusMetres } from 'client/store/drawSlice/drawSlice';
import { LINE } from 'client/utils/constants/symbolName';
import { LINE_STRING, MULTI_LINE_STRING } from 'client/utils/constants/geometryType';
import ServiceAreaOverlay from 'client/pages/MapPage/components/ServiceAreaOverlay/ServiceAreaOverlay';
import DATA_SENSITIVITY_LEVELS from 'client/utils/constants/dataSensitivityLevels';
import { ALL_CATEGORIES, HIDDEN_ASSETS_CATEGORY } from 'client/pages/MapPage/components/MapMenu/IsolatedViews/constants';

const { DEFAULT, DRAWING, MODIFYING, ADDING_OBSERVATION } = mapModes;
const drawSource = new VectorSource({ wrapX: false });
const HIDDEN_ASSETS_NONE = [];
const HIDDEN_ASSETS_OBSERVATION = [ASSET_TYPES.OBSERVATION];

export const shouldShowHiddenAssetsOverlay = ({
    isolatedViewOwnerId,
    isolatedViewAssetCategory,
    ownerHasHiddenAssets,
    redactedAssetsCount
}) => {
    if (!isolatedViewOwnerId) {
        return redactedAssetsCount > 0;
    }

    if (!ownerHasHiddenAssets) {
        return false;
    }

    return isolatedViewAssetCategory === ALL_CATEGORIES || isolatedViewAssetCategory === HIDDEN_ASSETS_CATEGORY;
};

const MapLayers = ({ handleChangeAOI, positionFeature, drawMobileArea, setDrawMobileArea }) => {
    const mapMode = useSelector(state => state.map?.mode);
    const bufferRadiusMetres = useSelector(selectBufferRadiusMetres);
    const aoiSource = useSelector(state => state.aoi?.source);
    const hideBasemap = useSelector(state => state.map?.hideBasemap);
    const isolatedViewOwnerId = useSelector(state => state.map?.ownerId);
    const isolatedViewAssetCategory = useSelector(state => state.map?.isolatedViewsAssetCategory);
    const showDimensions = useSelector(state => state.map?.showDimensions);
    const showAnnotations = useSelector(state => state.map?.showAnnotations);
    const sensitiveAssets = useSelector(state => state?.assets?.sensitiveAssets) || [];
    const { redactedAssetsCount, enhancedMeasuresCount } = useEnhancedMeasures();
    const selectedRedactedAsset = useSelector(state => state.map?.selectedRedactedAsset);
    const maxDrawExtent = useMaxDrawExtent();
    const lineDrawDimensions = useLineDrawDimensions();
    const { map } = useContext(MapContext);
    const { epsgCode } = useLoaderData() || {};

    const [highContrast] = useDarkMode();
    const additionalBaseMapClass = highContrast ? styles.highContrastBaseMap : '';
    const additionalLayerClass = highContrast ? styles.highContrastLayer : '';

    const baseMapSource = useBaseMap();

    const aoiBorderStyleFn = useMemo(() => {
        const hasEnhancedMeasures = enhancedMeasuresCount > 0;
        const fn = (feature) => (
            hasEnhancedMeasures
                ? hazardTapeBorderStyle(feature, bufferRadiusMetres)
                : borderStyle(feature, bufferRadiusMetres)
        );
        fn.toString = () => `aoi-border-style:${hasEnhancedMeasures ? 1 : 0}:${bufferRadiusMetres}`;
        return fn;
    }, [enhancedMeasuresCount, bufferRadiusMetres]);

    const aoiEnhancedMeasuresBackdropStyleFn = useMemo(() => {
        const fn = (feature) => enhancedMeasuresBackdropStyle(feature, 0.5, bufferRadiusMetres);
        fn.toString = () => `aoi-em-backdrop:0.5:${bufferRadiusMetres}`;
        return fn;
    }, [bufferRadiusMetres]);

    const aoiEnhancedMeasuresBackdropSelectedStyleFn = useMemo(() => {
        const fn = (feature) => enhancedMeasuresBackdropStyle(feature, 0.1, bufferRadiusMetres);
        fn.toString = () => `aoi-em-backdrop:0.1:${bufferRadiusMetres}`;
        return fn;
    }, [bufferRadiusMetres]);

    const ownerHasHiddenAssets = useMemo(() => {
        if (!isolatedViewOwnerId) {
            return false;
        }

        return sensitiveAssets.some(asset => {
            return asset?.dataproviderid_fk === isolatedViewOwnerId &&
                asset?.datasensitivitylevel === DATA_SENSITIVITY_LEVELS.REQUIRES_REDACTION;
        });
    }, [isolatedViewOwnerId, sensitiveAssets]);

    const showHiddenAssetsOverlay = useMemo(() => {
        return shouldShowHiddenAssetsOverlay({
            isolatedViewOwnerId,
            isolatedViewAssetCategory,
            ownerHasHiddenAssets,
            redactedAssetsCount
        });
    }, [isolatedViewAssetCategory, isolatedViewOwnerId, ownerHasHiddenAssets, redactedAssetsCount]);

    // Allow clearing off-screen aoi if in modifying state.
    useEffect(() => {
        if (!mapMode.includes(MODIFYING)) {
            return;
        }
        const drawFeatures = map.getLayerById('draw')?.getSource()?.getFeatures();
        if (drawFeatures && drawFeatures.length > 0) {
            const geomType = drawFeatures[0].getGeometry?.()?.getType?.();
            const isLine = geomType === LINE_STRING || geomType === MULTI_LINE_STRING;
            const limit = isLine ? lineDrawDimensions.length : maxDrawExtent;
            const drawType = isLine ? LINE : undefined;
            modifyStyle(drawFeatures[0], limit, bufferRadiusMetres, drawType);
        }
    }, [mapMode, lineDrawDimensions, maxDrawExtent, bufferRadiusMetres, map]);

    // Set layer names for annotations/dimensions based on EPSG code
    const northernIreland = epsgCode && isNi(epsgCode);
    const annotationsLayerName = northernIreland ? NI_ANNOTATIONS_LAYER_NAME : ANNOTATIONS_LAYER_NAME;
    const dimensionsLayerName = northernIreland ? NI_DIMENSIONS_LAYER_NAME : DIMENSIONS_LAYER_NAME;

    return <Layers>
        {baseMapSource && <TileLayer zIndex={LAYER_CONFIG.baseMap.zIndex} id={LAYER_CONFIG.baseMap.id} className={additionalBaseMapClass} source={baseMapSource} visible={!hideBasemap} />}
        <CustomBackdropMap zIndex={LAYER_CONFIG.backdropMap.zIndex} className={additionalLayerClass} />
        {epsgCode && <CustomVectorSource className={additionalLayerClass} clippingGeometry={aoiSource?.getFeatures()[0]?.getGeometry()} visible={showAnnotations} ownerId={isolatedViewOwnerId} typeName={annotationsLayerName} zIndex={LAYER_CONFIG.annotations.zIndex} />}
        {aoiSource && (
            <VectorLayer
                className={additionalBaseMapClass}
                zIndex={LAYER_CONFIG.aoi.zIndex}
                id={LAYER_CONFIG.aoi.id}
                visible={mapMode.includes(DEFAULT)}
                source={aoiSource}
                declutter
                style={aoiBorderStyleFn}
            />
        )}
        {aoiSource && <VectorLayer className={additionalBaseMapClass} zIndex={LAYER_CONFIG.aoiBackdropBase.zIndex} id={LAYER_CONFIG.aoiBackdropBase.id} visible={mapMode.includes(DEFAULT)} source={aoiSource} declutter style={backdropStyle} />}
        {aoiSource && showHiddenAssetsOverlay && (
            <VectorLayer
                className={additionalBaseMapClass}
                zIndex={LAYER_CONFIG.aoiBackdropHiddenAssets.zIndex}
                id={LAYER_CONFIG.aoiBackdropHiddenAssets.id}
                visible={mapMode.includes(DEFAULT)}
                source={aoiSource}
                declutter
                style={aoiEnhancedMeasuresBackdropStyleFn}
            />
        )}
        {aoiSource && showHiddenAssetsOverlay && (
            <VectorLayer
                className={additionalBaseMapClass}
                zIndex={LAYER_CONFIG.aoiBackdropHiddenAssetsSelected.zIndex}
                id={LAYER_CONFIG.aoiBackdropHiddenAssetsSelected.id}
                visible={mapMode.includes(DEFAULT) && Boolean(selectedRedactedAsset) && showHiddenAssetsOverlay}
                source={aoiSource}
                declutter
                style={aoiEnhancedMeasuresBackdropSelectedStyleFn}
            />
        )}
        <ServiceAreaOverlay epsgCode={epsgCode} />
        <VectorLayer zIndex={LAYER_CONFIG.draw.zIndex} id={LAYER_CONFIG.draw.id} visible={mapMode.includes(DRAWING) || mapMode.includes(MODIFYING)} source={drawSource} style={feature => {
            const currentBuffer = store.getState()?.draw?.bufferRadiusMetres;
            const buffer = Number.isFinite(currentBuffer) ? currentBuffer : bufferRadiusMetres;
            const geomType = feature?.getGeometry?.()?.getType?.();
            const isLine = geomType === LINE_STRING || geomType === MULTI_LINE_STRING;
            const limit = isLine ? lineDrawDimensions.length : maxDrawExtent;
            const drawType = isLine ? LINE : undefined;
            return modifyStyle(feature, limit, buffer, drawType);
        }} declutter />
        <AssetLayers
            className={highContrast ? styles.highContrastLayer : ''}
            hiddenAssets={mapMode.includes(ADDING_OBSERVATION) ? HIDDEN_ASSETS_OBSERVATION : HIDDEN_ASSETS_NONE}
        />
        <OneTapAOIIndicator className={additionalLayerClass} updateAoi={handleChangeAOI} drawMobileArea={drawMobileArea} setDrawMobileArea={setDrawMobileArea}></OneTapAOIIndicator>
        <VectorLayer
            zIndex={LAYER_CONFIG.locationMarker.zIndex}
            id={LAYER_CONFIG.locationMarker.id}
            source={new VectorSource({ features: [positionFeature].filter(e => e) })}
            style={feature => positionMarkerStyle(feature)}
        />
        {epsgCode && <CustomVectorSource className={additionalLayerClass} clippingGeometry={aoiSource?.getFeatures()[0]?.getGeometry()} visible={showDimensions} ownerId={isolatedViewOwnerId} typeName={dimensionsLayerName} zIndex={LAYER_CONFIG.dimensions.zIndex} />}
    </Layers>;
};
export default MapLayers;
